import { QuotationRequest, QuotationRequestStatusEnum, Requirement, RequirementItem } from "@/types/requirement";
import { App, Button, Flex, Modal, notification, Select, Space, Spin, Steps, Tabs, Typography } from "antd";
import { FC, useEffect, useMemo, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import ContentWrap from "./style";
import { PurchaseOrderStateEnum, Supplier, SupplierPrice } from "@/types/purchase";
import { getSupplierName } from "@/utils/supplier";
import SOrgSelect from "@/components/SOrgSelect";
import { Org } from "@/types/org";
import { orgList as orgListApi } from "@/apis/org";
import SupplierList from "./components/SupplierList";
import MatchProduct, { MatchProductRef } from "./components/MatchProduct";
import SelectCustomer from "@/components/Selector/SelectCustomer";
import SaleDetail from "./components/SaleDetail";
import { Customer, SaleOrderAdd, SaleOrderAddItem, PriceHis, PriceAdd, SaleOrder, SaleOrderItemState } from "@/types/sales";
import { sAdd as saleOrderAdd, update } from "@/apis/order";
import { latestPrices } from "@/apis/customer";
import { sAdd as priceAdd } from "@/apis/price";
import { useMainCurrency } from "@/layout/hooks/useMainCurrency";
import { divided, dp, minus, multi, plus } from "@/utils/calc";
import { ItemWithPrice } from "./components/GeneratePurchase";
import { acceptStateUpdate, supplierPricesFetch } from "@/apis/supplier";
import { getQuotationRequestTruePrice, getRequirementConfirmItem, getTrueCount } from "@/pages/Requirement/util";
import { addQuotationRequest, handleSelect, QuotationRequestsParams } from "@/apis/requirement";
import { quotationPurchase } from "@/apis/quotationRequestItem";
import type { QuotationPurchaseRound } from "@/apis/quotationRequestItem";
import { itemUpdateByKey } from "@/apis/purchase";
import orderApis from "@/apis/order";
import useRequirementParams from "@/pages/Requirement/hooks/useRequirementParams";
import apis from '@/apis/requirement';
import SText from "@/styles/SText";
import PurchasedQuotations from "./components/PurchasedQuotations";
import ExportProgressModal from "@/components/ExportProgressModal";
import { BASEURL } from "@/apis";



interface SwapSaleOrderProps {
  items: Array<RequirementItem>;
  open: boolean;
  onClose: () => void;
  saleOrder: SaleOrderItemState;
  requirement: Requirement;
  handleOk: () => void
}
const SwapSaleOrder: FC<SwapSaleOrderProps> = ({ items, open, onClose, saleOrder, requirement, handleOk }) => {
  const { t } = useTranslation();
  const { modal } = App.useApp();
  const { getMainCurrency } = useMainCurrency();
  const { params } = useRequirementParams(true);

  const matchProductRef = useRef<MatchProductRef>(null);
  const [current, setCurrent] = useState<number>(0);
  const [purchaseRound, setPurchaseRound] = useState<QuotationPurchaseRound>(0);

  const [supplier, setSupplier] = useState<Supplier | null>(null);
  const [customer, setCustomer] = useState<Customer | null>(null); // 记录上次请求组织的供应商ID

  const [orgListLoading, setOrgListLoading] = useState<boolean>(false);
  const [orgList, setOrgList] = useState<Org[]>([]);
  const [orgId, setOrgId] = useState<string>('');
  const [lastFetchSupplierId, setLastFetchSupplierId] = useState<string>(''); // 记录上次请求组织的供应商ID

  const [saleAddLoading, setSaleAddLoading] = useState<boolean>(false);
  const [resSaleOrder, setResSaleOrder] = useState<SaleOrderItemState | null>(null);
  const [matchErr, setMatchErr] = useState<boolean>(false);

  const [errCodes, setErrCodes] = useState<Array<string>>([]);
  const [purchasedQuotations, setPurchasedQuotations] = useState<Array<QuotationRequest>>([]);

  const [showProcess, setShowProcess] = useState<boolean>(false);
  const [process, setProcess] = useState<number>(0);

  useEffect(() => {
    notification.info({
      message: t("requirement.noDataToTransferDesc"),
      placement: "top"
    })
  }, [])

  useEffect(() => {
    // 当进入步骤1且供应商存在时，检查是否需要请求组织列表
    if (supplier && current === 1) {
      // 如果切换了供应商，需要重新请求
      if (supplier.id !== lastFetchSupplierId) {
        fetchOrgList(supplier);
      }
    }
  }, [current, supplier, lastFetchSupplierId])

  const fetchOrgList = async (supp: Supplier) => {
    const supplierApi = supp?.wmsApi ?? ''
    setOrgId(''); // 切换供应商后清空组织选择
    setOrgList([]);
    setCustomer(null);
    setLastFetchSupplierId(supp.id); // 记录已请求的供应商ID
    if (!!!supplierApi) {
      notification.error({
        message: t("requirement.supplierApiError"),
        description: `${t("requirement.supplierApiLabel")}${supplierApi}`,
        placement: "top",
      })
      return;
    }

    setOrgListLoading(true);
    try {
      const res = await orgListApi(undefined, supplierApi);
      setOrgList(res.body);
    } catch {
      notification.error({
        message: t("requirement.fetchOrgInfoFailed"),
        placement: "top"
      })
    } finally {
      setOrgListLoading(false);
    }
  };

  const handleClose = () => {
    if (purchasedQuotations.length > 0) handleOk();
    onClose();
  }

  const getPrice = (price: SupplierPrice | undefined, count: string) => {
    let trueCount = getTrueCount(count, price?.mpq ?? '1')
    let supplierPrice = price?.ladders
      .sort((a, b) => a.count - b.count)
      .find(price => price.count <= Number(trueCount))?.price;
    return Number(supplierPrice ?? '1')
  }

  const generateQuoAdd = (item: ItemWithPrice): QuotationRequestsParams => {
    const price = item.price;
    return {
      deadline: requirement.mrd || 0,
      mrd: requirement.mrd || 0,
      supplierPrice: item.priceValue || null,
      supplierTime: requirement.mrd ? requirement.mrd - 1 : 0,
      mpq: price?.mpq ?? "",
      days: price?.days ?? 0,
      ddl: price?.expiredAt ?? null,
      spareCount: "0",
      spareRate: "0",
    };
  };
  // 通过问询单
  // const confirmPass = async (req: Requirement) => {
  //   if (requirement && requirement.saleOrder) {  //物控手动添加的没有销售单，不用update
  //     let bomCost = 0;
  //     req.items.forEach(quo => {
  //       if (quo.equotationRequest) {
  //         bomCost += getQuotationRequestTruePrice(quo.equotationRequest)
  //       }
  //     })
  //     let netProfit = plus(minus(
  //       minus(requirement.saleOrder.totalRealAmount, bomCost + ''),
  //       divided(multi(requirement.saleOrder.commissionRate || '', requirement.saleOrder.totalRealAmount), '100')
  //     ), requirement.saleOrder.extPrice + '')
  //     let saleOrderInfo: SaleOrderAdd = {
  //       ...requirement.saleOrder,
  //       shippingMethod: requirement.saleOrder.shippingMethod || '',
  //       otherNumber1: requirement.saleOrder.otherNumber1 || '',
  //       discount: requirement.saleOrder.discount || '',
  //       mfgr: requirement.saleOrder?.mfgr || '',
  //       customerModel: requirement.saleOrder?.customerModel || '',
  //       urgencyLevel: requirement.saleOrder?.urgencyLevel || '',
  //       specialReq: requirement.saleOrder?.specialReq || '',
  //       bomCost: bomCost + '',      //TODO转汇率
  //       netProfit,
  //       items: [],
  //       // netProfit: (
  //       //   Number(detail.saleItem.totalRealAmount) - bomCost - (
  //       //     Number(detail.saleItem.order.commissionRate || '') * Number(detail.saleItem.totalRealAmount) / 100
  //       //   ) + Number(detail.saleItem.order.extPrice || '')) + '',
  //     }
  //     await orderApis.update(requirement.saleOrder?.id ?? '', saleOrderInfo)
  //   }
  //   params && params.confirmType && (await apis.confirms(requirement.id, getRequirementConfirmItem(requirement.confirms ?? [], params.confirmType, "next", '')))
  //   // handleOk('del')
  // }

  const handleGenerateSale = async () => {
    if (!customer || !supplier) {
      notification.error({
        message: t("requirement.selectSupplierAndCustomer"),
        placement: "top"
      })
      return;
    }

    const matchedProducts = matchProductRef.current?.getMatchedProducts() || [];

    if (matchedProducts.length === 0) {
      notification.error({
        message: t("requirement.noMatchedProductData"),
        placement: "top"
      })
      return;
    }

    setSaleAddLoading(true);
    let succQuotations: QuotationRequest[] = [];
    setPurchasedQuotations([]);
    let eSupplierCodes: Array<string> = [];
    let eCustomerCodes: Array<string> = [];
    try {
      // 1、匹配供应商价格
      const codes = items.map(item => item.product?.code).filter(Boolean) as string[];
      if (codes.length === 0) {
        setSaleAddLoading(false);
        notification.error({ message: t('requirement.swapEnd'), });
        return
      }
      let priceItems: Array<ItemWithPrice> = [];

      const { body: prices } = await supplierPricesFetch(supplier.id, {
        strs: codes,
      });
      const priceMap = new Map(prices.map(p => [p.product.code, p]));
      priceItems = items.map(item => ({
        ...item,
        price: priceMap.get(item.product?.code ?? ""),
        priceValue: getPrice(priceMap.get(item.product?.code ?? ""), item.purCount)
      }));
      let unMatchtedCodes = priceItems.filter(item => !!!item.price).map(item => item.product?.code ?? '')
      if (unMatchtedCodes.length > 0) {
        eSupplierCodes = unMatchtedCodes;
        notification.error({ message: t('requirement.swapEnd'), description: t('requirement.plAddSupplierPrice') });
        setSaleAddLoading(false);
      } else eSupplierCodes = []
      if (eSupplierCodes.length > 0) return setErrCodes(eSupplierCodes);

      // 2. 获取价格历史数据 (latestPrices: customer.id, undefined, supplier.wmsApi)
      let { body: priceHisList } = await latestPrices(customer.id, undefined, supplier.wmsApi);

      // 3. 找出 priceHisList 中不存在的物料
      const existingProductIds = new Set(priceHisList.map(p => p.productId));
      const productsNeedAddPrice = matchedProducts.filter(p => !existingProductIds.has(p.id));
      if (productsNeedAddPrice.length > 0) {
        eCustomerCodes = productsNeedAddPrice.map(p => p.code)
        notification.error({
          message: t('requirement.swapEnd'),
          description: (
            <>
              <div style={{ color: "red" }}>
                {t('requirement.noCustomerPrice')}
              </div>
              <div>{t('requirement.plAddCustomerPrice')}</div>
            </>
          )
        })
        setSaleAddLoading(false)
      } else eCustomerCodes = []

      if (eCustomerCodes.length > 0) return setErrCodes(eCustomerCodes);

      setShowProcess(true);
      // 4、生成问询单、选中问询单、生成采购单【本系统采购】
      for (let i = 0; i < priceItems.length; i++) {
        const item = priceItems[i];
        if (!item.id) {
          setProcess(Math.round(((i + 1) / priceItems.length) * 100));
          continue
        };
        try {
          const params = generateQuoAdd(item);
          let quoList: QuotationRequest[] = [];
          //生成问询单
          quoList = await addQuotationRequest(item.id, QuotationRequestStatusEnum.SUBMITED, params, [item.price?.priceId ?? '']);
          // 生成问询单为空并且已存在了问询单
          if ((!!!quoList || quoList.length === 0) && !!item.quotationRequest) quoList = [item.quotationRequest as QuotationRequest]
          if (quoList && quoList.length > 0) {
            //选中问询单
            await handleSelect(quoList[0].id);
            //去采购【生成采购单】
            let purchasedQuotation: QuotationRequest[] = [];
            purchasedQuotation = await quotationPurchase([quoList[0].id], undefined, purchaseRound);
            //生成采购单为空并且该问询单已有采购单
            if ((!!!purchasedQuotation || purchasedQuotation.length === 0) && !!item.equotationRequest?.purchaseItem) purchasedQuotation = [item.equotationRequest as QuotationRequest]
            if (purchasedQuotation && purchasedQuotation.length > 0) {
              //下单【采购单item的orderState更新为1】
              let purItem = await itemUpdateByKey(purchasedQuotation[0].purchaseItem?.id ?? '', { key: "orderState", value: PurchaseOrderStateEnum.PAYED })
              // //协助接受【采购单acceptStatus更新为1】
              let purOrder = await acceptStateUpdate(purchasedQuotation[0].purchaseItem?.orderId ?? '', 1);
              let resQuuotation: QuotationRequest = {
                ...purchasedQuotation[0],
                purchaseItem: {
                  ...purItem,
                  order: purOrder
                },
                requirementItem: {
                  ...item
                }
              }
              succQuotations.push(resQuuotation)
              setProcess(Math.round(((i + 1) / priceItems.length) * 100));
              continue
            }
          }
        } catch (error) {
          console.error(`Failed to add quotation request for item ${item.product?.code}:`, error);
          setProcess(Math.round(((i + 1) / priceItems.length) * 100));
        }
      }
      setPurchasedQuotations(succQuotations);   //本系统采购结果
      // 获取币种
      const currencyDict = await getMainCurrency(supplier.wmsApi ?? '', [])

      // 3. 批量添加 Price
      // if (productsNeedAddPrice.length > 0) {
      //   for (const product of productsNeedAddPrice) {
      //     try {
      //       const priceAddData: PriceAdd = {
      //         customerId: customer.id,
      //         productId: product.id,
      //         price: '1',
      //         desc: '',
      //         estimateDays: null,
      //         expiredAt: null,
      //         mfgr: '',
      //         customerModel: '',
      //       };
      //       await priceAdd(priceAddData, supplier.wmsApi ?? "");
      //     } catch (error) {
      //       console.error(`Failed to add price for product ${product.code}:`, error);
      //     }
      //   }

      //   // 4. 重新获取价格历史数据
      //   const { body: newPriceHisList } = await latestPrices(customer.id, undefined, supplier.wmsApi);
      //   priceHisList = newPriceHisList;
      // }

      // 5. 构建 productId -> PriceHis 的映射
      const priceHisMap = new Map<string, PriceHis>();
      for (const priceHis of priceHisList) {
        priceHisMap.set(priceHis.productId, priceHis);
      }

      // 6. 遍历 matchedProducts 构建 SaleOrderAddItem 数据
      const saleOrderItems: SaleOrderAddItem[] = [];
      for (const matchedProduct of matchedProducts) {
        const priceHis = priceHisMap.get(matchedProduct.id);
        if (!priceHis) continue;

        const originalItem = items.find(item => item.product?.code === matchedProduct.code);

        const quantity = Number(originalItem?.purCount) || 0;
        const price = priceHis.price || '1';
        const amount = multi(quantity + '', price);
        // const totalRealAmount = divided(amount ?? '0', plus('1', divided(customer.taxRate || '0', '100')))

        const saleItem: SaleOrderAddItem = {
          priceHisId: priceHis.id,
          price,
          remark: '',
          productName: matchedProduct.name || '',
          quantity,
          discountedPrice: '0',
          amount: dp(amount),
          discount: '0',
          realAmount: dp(amount),
          bquantity: 0,
          bdiscountedPrice: '0',
          bamount: '0',
          bdiscount: '0',
          brealAmount: '0',
          totalRealAmount: dp(amount),
        };
        saleOrderItems.push(saleItem);
      }

      // 7. 构建 SaleOrderAdd 数据
      // 累加所有 item 的 totalRealAmount
      let totalRealAmountSum = '0';
      saleOrderItems.forEach(item => {
        totalRealAmountSum = plus(totalRealAmountSum, item.totalRealAmount || '0');
      });

      const amount = divided(totalRealAmountSum, plus('1', divided(customer.taxRate || '0', '100')));
      let orderTotalAmount = '0';
      saleOrderItems.forEach(item => {
        orderTotalAmount = plus(item.totalRealAmount, orderTotalAmount)
      })

      const saleOrderAddData: SaleOrderAdd = {
        docId: '',
        type: '1',
        customerId: customer.id,
        country: customer.country || '',
        taxRate: customer.taxRate || '',
        receiver: customer.shortName || customer.name1 || customer.name2 || customer.code || '',
        receiverContact: customer.contact || '',
        receiverMobile: customer.mobile || '',
        receiverAddress: customer.address || '',
        totalAmount: dp(amount),
        realAmount: amount,
        preApproval: 0,
        innerNumber: saleOrder?.innerNumber ?? '',
        finishState: 0,
        extPrice: '0',
        items: saleOrderItems,
        shippingMethod: '',
        currency: customer.currency || currencyDict?.key || '2',
        commissionRate: customer.commissionRate ? String(customer.commissionRate) : '',
        otherNumber1: saleOrder?.number,
        productId: null,
        preFinishDate: null,
        reqDeliDate: saleOrder.reqDeliDate || null,
        smtStartDate: null,
        totalRealAmount: orderTotalAmount,
        mfgr: '',
        customerModel: '',
        urgencyLevel: '',
        specialReq: '',
        sourceServer: BASEURL,
        sourceOrder: requirement.number
      };
      let saleOrderRes = await saleOrderAdd(saleOrderAddData, orgId, supplier.wmsApi ?? '');
      await update(saleOrder?.id ?? "", {
        ...saleOrder,
        shippingMethod: saleOrder.shippingMethod ?? '',
        discount: saleOrder.discount ?? '',
        otherNumber1: saleOrderRes.number,
        mfgr: saleOrderRes?.mfgr || '',
        customerModel: saleOrderRes?.customerModel || '',
        urgencyLevel: saleOrderRes?.urgencyLevel || '',
        specialReq: saleOrderRes?.specialReq || '',
        items: []
      })
      // 通过问询单
      // await confirmPass({
      //   ...requirement,
      //   items: items.map(item => {
      //     return {
      //       ...item,
      //       equotationRequest: succQuotations.find(quo => quo.requirementItemId === item.id) || null
      //     }
      //   })
      // })
      notification.success({
        message: t("requirement.saleOrderTransferSuccess"),
        description: `${t("requirement.saleOrderNumber")}${saleOrderRes.number}`,
        placement: "top",
      })
      setResSaleOrder({ ...saleOrderRes });
      setCurrent(current + 1);

    } catch (error) {
      console.error('Failed to generate sale order:', error);
      notification.error({
        message: t("requirement.saleOrderGenerateFailed"),
        placement: "top"
      });
    } finally {
      setSaleAddLoading(false);
      setTimeout(() => {
        setShowProcess(false);
        setProcess(0);
      }, 300)
    }
  };

  const handlePrev = () => {
    setCurrent(current - 1)
    setMatchErr(false);
  }

  const handleNext = () => {
    if (items.length === 0) return notification.error({
      message: t("requirement.noDataToTransfer"),
      description: t("requirement.noDataToTransferDesc"),
      placement: "top"
    })
    if (!!!supplier) {
      notification.error({
        message: t("requirement.selectSupplierFirst"),
        placement: "top"
      })
      return
    }

    // if (!!!supplier.wmsApi || !)) {
    //   notification.error({
    //     message: t("requirement.supplierApiError"),
    //     placement: "top"
    //   })
    //   return
    // }
    if (!!!orgId && current > 0) {
      notification.error({
        message: t("requirement.selectSupplierOrg"),
        placement: "top"
      })
      return
    }
    if (!!!customer && current > 0) {
      notification.error({
        message: t("requirement.selectSupplierCustomer"),
        placement: "top"
      })
      return
    }
    // current === 1 时打印匹配的产品数据
    if (current === 1) {
      modal.confirm({
        title: t("requirement.swapSale"),
        content: t("requirement.confirmGenerateSaleOrder"),
        onOk: handleGenerateSale
      })
      return
    }
    setCurrent(current + 1)
  }

  const handleCustomer = (customerSelected: Customer) => {
    setCustomer(customerSelected)
  }

  const { disNext, disPrev } = useMemo(() => {

    let disNext = false;
    let disPrev = false;
    return {
      disNext: disNext || matchErr,
      disPrev
    }
  }, [current, supplier, orgId, matchErr]);

  const stepsItems = useMemo(() => [
    {
      title: t("requirement.selectSupplier"),
      content: <SupplierList value={supplier} onChange={setSupplier} />,
    },
    {
      title: t("requirement.selectSupplier"),
      content: <MatchProduct ref={matchProductRef} items={items} supplierApi={supplier?.wmsApi ?? ''} setMatchErr={val => setMatchErr(val)} errCodes={errCodes} />,
    },
    {
      title: t("requirement.complete"),
      content: (
        <Tabs style={{ height: "100%" }}
          type="card" items={[
            {
              label: <SText text={t('purchase.hasPurOrder')} />,
              key: 'requirementList',
              children: <PurchasedQuotations items={purchasedQuotations} />
            },
            {
              label: <SText text={t('warehouse.salesOrder')} />,
              key: 'preOutInventory',
              children: <SaleDetail saleOrder={resSaleOrder} />
            },

          ]}
        />
      ),
    }
  ], [supplier, items, resSaleOrder, errCodes]);

  const footer = (
    <Flex justify="space-between">
      <Space>
        {current > 0 && current !== 2 && <Button disabled={disPrev} onClick={handlePrev}>{t("requirement.prevStep")}</Button>}
        {current !== 2 && <Button disabled={disNext} type="primary" onClick={handleNext}>{t("requirement.nextStep")}</Button>}
      </Space>
      <Space>
        <Button onClick={handleClose}>{t("requirement.close")}</Button>
      </Space>
    </Flex>
  );
  return (
    <Modal title={t("requirement.swapSale")} open={open}
      onCancel={handleClose}
      centered
      width="80vw"
      footer={footer}
    >
      <Spin spinning={saleAddLoading}>
        <ContentWrap>
          <Steps current={current} items={stepsItems} />
          {current !== 0 && supplier && <Flex gap={20}>
            <Typography.Title level={4} style={{ marginTop: 8 }} >{t('purchase.supplierPrint')}{getSupplierName(supplier)}</Typography.Title>
            <Space>
              <Typography.Title level={5} style={{ marginTop: 8 }} >{t('requirement.orgLabel')}</Typography.Title>
              <SOrgSelect disabled={current === 2} allowClear={false} onChange={id => {
                setOrgId(id);
                setCustomer(null)
              }} value={orgId || undefined} list={orgList} loading={orgListLoading} style={{ minWidth: 220 }} />
            </Space>
            {!!orgId && supplier && <Space>
              <Typography.Title level={5} style={{ marginTop: 8 }} >{t('sale.customerInfo')}：</Typography.Title>
              <SelectCustomer supplierApi={supplier.wmsApi} orgId={orgId} disabled={current === 2 || !!!orgId} onChangeCustomer={handleCustomer} value={customer?.id ?? undefined} style={{ minWidth: 220 }} defaultList={customer ? [customer] : undefined} />
            </Space>}
            {current === 1 && (
              <Space>
                <Typography.Title level={5} style={{ marginTop: 8 }} >{t("purchase.roundMethod")}</Typography.Title>
                <Select<QuotationPurchaseRound>
                  value={purchaseRound}
                  style={{ minWidth: 120 }}
                  options={[
                    { label: t("purchase.roundNone"), value: 0 },
                    { label: t("purchase.roundDown"), value: 1 },
                    { label: t("purchase.roundUp"), value: 2 },
                    { label: t("purchase.roundNearest"), value: 3 },
                  ]}
                  onChange={setPurchaseRound}
                />
              </Space>
            )}
          </Flex>}
          <div className="body-content">
            {stepsItems[current].content}
          </div>
        </ContentWrap>
      </Spin>
      <ExportProgressModal
        visible={showProcess}
        progress={process}
        title={t("requirement.swapSale") + '...'}
        processLabel={t("requirement.swapSale")}
      />
    </Modal>
  )
};

export default SwapSaleOrder;
